import React, { useEffect, useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { generateClient } from "@aws-amplify/api";
import { getDeals } from "@/graphql/queries";
import RootContext from "@/RootContext";
import useSubjectAPI from "./useSubjectAPI";

const client = generateClient();

const DealsSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [open, setOpen] = useState(true);
  const [availableDeals, setAvailableDeals] = useState([]);

  const [dealsList, setDealsList] = useState(
    isCreate ? [{ deal_id: "", status: "Leads" }] : [],
  );

  const [editingMap, setEditingMap] = useState({});
  const [originalValues, setOriginalValues] = useState({});

  const purple = "#2E2868";
  const pillBg = "#ffffff";

  const rootContext = useContext(RootContext);
  const statusOptions = rootContext.get("deal_statuses") || [];

  /* LOAD OPTIONS */
  useEffect(() => {
    client
      .graphql({ query: getDeals })
      .then((res) => setAvailableDeals(res.data?.getDeals?.deals || []));
  }, []);

  /* LOAD DATA (Edit Mode Only) */
  useEffect(() => {
    if (isCreate) return;
    loadSubject().then((res) => {
      setDealsList(res?.subject_deal_status || []);
    });
  }, [subjectId, isCreate]);

  /* SYNC TO FORM (Create Mode Only) */
  useEffect(() => {
    if (!isCreate || !form?.setValue) return;
    form.setValue({
      key: "subject_deal_status",
      value: dealsList.map((d) => ({
        deal_id: Number(d.deal_id) || null,
        status: d.status || null,
      })),
    });
  }, [dealsList, isCreate, form]);

  /* HANDLERS */
  const toggleEdit = (index, field, status) => {
    if (isCreate) return;
    const editKey = `${index}-${field}`;
    if (status === true) {
      setOriginalValues((prev) => ({
        ...prev,
        [editKey]: dealsList[index]?.[field],
      }));
    }
    setEditingMap((prev) => ({ ...prev, [editKey]: status }));
  };

  const handleCancel = (index, field) => {
    const editKey = `${index}-${field}`;
    const originalValue = originalValues[editKey];
    const updated = [...dealsList];
    updated[index] = { ...updated[index], [field]: originalValue };
    setDealsList(updated);
    setEditingMap((prev) => ({ ...prev, [editKey]: false }));
  };

  const handleChange = (index, field, val) => {
    const value = field === "deal_id" ? Number(val) : val;
    const updated = [...dealsList];
    if (!updated[index]) updated[index] = {};
    updated[index] = { ...updated[index], [field]: value };
    setDealsList(updated);
  };

  const saveField = async (index, field) => {
    const editKey = `${index}-${field}`;

    const enrollPayload = dealsList.map((d, idx) => {
      const baseObj = {
        subject_id: subjectId,
        deal_id: Number(d.deal_id) || null,
        status: d.status || null,
      };

      if (d.id) baseObj.id = d.id;
      return baseObj;
    });

    const payload = {
      subjects: { id: subjectId },
      subject_deal_status: enrollPayload,
    };

    try {
      setEditingMap((prev) => ({ ...prev, [editKey]: false }));
      const res = await saveSubjectDirect(payload);

      if (res?.subject_deal_status) {
        setDealsList(res.subject_deal_status);
      }
    } catch (err) {
      alert("Save failed");
      console.error(err);
      handleCancel(index, field);
    }
  };

  const handleAdd = () => {
    setDealsList((prev) => [...prev, { deal_id: "", status: "Leads" }]);
    setOpen(true);
  };

  const handleRemove = async (index) => {
    const itemToRemove = dealsList[index];
    if (!window.confirm("Remove this deal?")) return;

    const newList = dealsList.filter((_, i) => i !== index);
    setDealsList(newList);

    if (!isCreate && itemToRemove.id) {
      try {
        await saveSubjectDirect(
          { subjects: { id: subjectId } },
          {
            input_delete: [{ id: itemToRemove.id, key: "subject_deal_status" }],
          },
        );
      } catch (err) {
        console.error("Delete failed", err);
        setDealsList(dealsList);
      }
    }
  };

  const renderSelect = (index, label, field, options, value) => {
    const editKey = `${index}-${field}`;
    const isEditing = editingMap[editKey];
    const isInputActive = isCreate || isEditing;

    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      border: isCreate ? "1px solid #ced4da" : "none",
    };

    const inputStyle = {
      backgroundColor: isInputActive ? "#fff" : pillBg,
      border: isInputActive ? `2px solid ${purple}` : "0",
      boxShadow: isInputActive ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isInputActive
        ? "0.5rem 105px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    const selectedOption = options.find(
      (o) => String(o.value) === String(value),
    );
    const displayValue = selectedOption ? selectedOption.label : value;

    return (
      <div className="mb-3">
        <label className="form-label mb-1 fw-bold" style={{ fontSize: "13px" }}>
          {label}
        </label>
        <div style={{ position: "relative" }}>
          {isCreate && (
            <select
              value={value || ""}
              onChange={(e) => handleChange(index, field, e.target.value)}
              className="form-select rounded-pill"
              style={inputStyle}
            >
              <option value="">Select...</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}

          {!isCreate && !isEditing && (
            <>
              <div
                className="form-control rounded-pill border-0"
                style={viewStyle}
                title={displayValue}
              >
                {displayValue || "Select..."}
              </div>
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(index, field, true)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: 14,
                  zIndex: 2,
                }}
              />
            </>
          )}

          {!isCreate && isEditing && (
            <>
              <select
                value={value || ""}
                onChange={(e) => handleChange(index, field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
                autoFocus
              >
                <option value="">Select...</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: "flex",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => saveField(index, field)}
                  className="btn btn-sm"
                  style={{
                    background: purple,
                    color: "#fff",
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel(index, field)}
                  className="btn btn-sm btn-light"
                  style={{
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          onClick={() => setOpen((prev) => !prev)}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <div className="d-flex align-items-center">
            <span
              style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}
            >
              ⋮⋮
            </span>
            <h5 className="fw-bold m-0" style={{ color: purple }}>
              Deal(s)
            </h5>
            <span className="badge ms-2 rounded-pill bg-light text-dark border">
              {dealsList.length}
            </span>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "14px" }} />
          </div>
        </div>

        {open && (
          <>
            {dealsList.map((item, i) => (
              <div key={i} className="mb-4 position-relative">
                <div
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 5 }}
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      color: purple,
                    }}
                    onClick={() => handleRemove(i)}
                  />
                </div>
                {renderSelect(
                  i,
                  "Deal",
                  "deal_id",
                  availableDeals.map((d) => ({
                    value: d.id,
                    label: d.deal_name,
                  })),
                  item.deal_id,
                )}
                {renderSelect(
                  i,
                  "Status",
                  "status",
                  statusOptions.map((s) => ({
                    value: s.id,
                    label: s.label || s.id,
                  })),
                  item.status,
                )}
                {i < dealsList.length - 1 && (
                  <hr className="my-4" style={{ borderColor: "#eee" }} />
                )}
              </div>
            ))}
            <div className="text-center mt-3">
              <button
                onClick={handleAdd}
                className="btn btn-sm"
                style={{
                  border: `1px dashed ${purple}`,
                  color: purple,
                  borderRadius: "20px",
                  padding: "5px 20px",
                  backgroundColor: "transparent",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Another
                Deal
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DealsSection;
